<script setup lang="ts">
import { onMounted, ref, watch, computed } from 'vue'
import { signUpAdmin, confirmSignUpAdmin } from '@/services/authService'
import { useDisplay } from 'vuetify'
import router from '@/router'
import { useAccountStore } from '@/stores/accountStore'
import { SendUserName, VerifyAdmin } from '@/api/accountsAdmin'
import Cookies from 'universal-cookie'

const { xs } = useDisplay() // Responsive adjustments

const email = ref('')
const username = ref('')
const password = ref('')
const groupToSend = ref('')

const isEmailSent = ref(false)
const message = ref('')
const snackbar = ref(false)
const allAccount = ref([])
const searchQuery = ref('')
const couponId = ref('')
const loadingCouponId = ref(false)
import { useUserStore } from '@/stores/userStore'

const showPassword = ref(false)
const showPasswordConfirm = ref(false)
const confirmPassword = ref('')
const passwordError = ref<string | null>(null)

const userStore = useUserStore()
const loading = ref(false)
const accountStore = useAccountStore()

const cookies = new Cookies()

// const loadAccount = async () => {

//   try {
//     const data = await fetchAccounts();
//     // console.log("Fetched accounts:", data);.

//     allAccount.value = data.map((acc) => ({
//       accountNameFriendly: acc.accountNameFriendly,

//     }))

//     console.log(allAccount.value)

//   } catch (error) {
//     console.error("Failed to load all Users:", error);
//   } finally {

//   }
// };

// onMounted(loadAccount)

const handleSignUp = async () => {
  loading.value = true

  try {
    if (!username.value || !email.value || !password.value || !confirmPassword) {
      message.value = '❌ Please fill all the fields'
      snackbar.value = true
      loading.value = false

      return
    }

    if (!email.value.endsWith('@jazel.com') && !email.value.endsWith('@10thdegree.com')) {
      message.value = '❌ Email must be a @jazel.com or @10thdegree.com address.'
      snackbar.value = true
      loading.value = false

      return
    }
    if (confirmPassword.value !== password.value) {
      passwordError.value = 'Passwords do not match'
      message.value = '❌ New password and confirmation do not match.'
      snackbar.value = true
      loading.value = false
      return
    }
    await VerifyAdmin()
    await signUpAdmin(username.value, email.value, password.value)
    await SendUserName(username?.value, groupToSend.value)
    // console.log(username.value, email.value, password.value, juid.value)
    message.value = '✅ Verification email sent. Please click the link in your inbox to confirm.'
    isEmailSent.value = true
    snackbar.value = true
    // Clear cached company users so the list refreshes next visit
    try {
      accountStore.setCompanyUsersCache([])
    } catch {}
  } catch (error: any) {
    console.log('===========', error)
    message.value = error.message || '❌ Signup failed.'
    snackbar.value = true

    loading.value = false
  }
}

const getCouponId = async () => {
  try {
    loadingCouponId.value = true
    const data = await fetchAccountSingle(juid.value)
    console.log(data?.couponId || 'did not get coupon id')
    couponId.value = data?.couponId || ''
    loadingCouponId.value = false
  } catch (error: any) {
    message.value = error.message || ''
    snackbar.value = true
    loadingCouponId.value = false
  }
}

const goBackAfterEmail = () => {
  try {
    accountStore.setCompanyUsersCache([])
  } catch {}
  router.push('/company-accounts')
}

// Validation rules
const required = (v: string) => !!v || 'This field is required'
const emailRules = [
  (v: string) => !!v || 'Email is required',
  (v: string) => /.+@.+\..+/.test(v) || 'Enter a valid email',
]

const allGroups = ['company_user', 'company_admin', 'company_suadmin', 'company_owner']

const availableGroups = computed(() => {
  switch (userStore?.role) {
    case 'company_owner':
      return allGroups
    case 'company_suadmin':
      // suadmin sees user, admin, suadmin
      return allGroups.filter((g) => g !== 'company_owner')
    case 'company_admin':
      // admin sees user + admin
      return allGroups.filter((g) => ['company_admin', 'company_user'].includes(g))
    default:
      // a normal user only sees themselves
      return ['company_user']
  }
})

// Show friendly labels while keeping raw keys as values
function toFriendlyRoleName(roleKey: string) {
  if (!roleKey) return ''
  const key = String(roleKey).replace(/^(company|client)_/, '')
  const map: Record<string, string> = {
    owner: 'Owner',
    suadmin: 'Super Admin',
    admin: 'Admin',
    manager: 'Manager',
    user: 'User',
  }
  return map[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
}
</script>

<template>
  <div class="page-wrapper">
    <v-container class="auth-container" fluid>
      <h2 class="brand">SodaSoft.io</h2>
      <v-card class="auth-card" :max-width="xs ? '100%' : 500">
        <div class="text-center mb-6">
          <h1 class="welcome">
            {{
              isEmailSent ? 'Check your email to verify your account' : 'Create a Company Account'
            }}
          </h1>

          <!-- <p class="subtitle mt-4">
            Create company account<br />
            
          </p> -->
        </div>

        <v-card-text style="width: 100%; min-width: 350px">
          <v-form @submit.prevent="isEmailSent ? goBackAfterEmail() : handleSignUp()">
            <div class="input-div">
              <input
                type="text"
                v-if="!isEmailSent"
                placeholder="Username"
                v-model="username"
                class="input"
                outlined
                :rules="[required]"
              />
              <input
                type="email"
                v-if="!isEmailSent"
                placeholder="Email"
                v-model="email"
                class="input"
                outlined
                :rules="[required]"
              />

              <div class="password-wrapper" v-if="!isEmailSent">
                <input
                  :type="showPassword ? 'text' : 'password'"
                  v-model="password"
                  placeholder="Password"
                  :class="['input', { 'is-invalid': passwordError }]"
                  @input="passwordError = null"
                />
                <button type="button" class="eye-btn" @click="showPassword = !showPassword">
                  <i :class="showPassword ? 'mdi mdi-eye-off' : 'mdi mdi-eye'"></i>
                </button>
              </div>
              <div class="password-wrapper" v-if="!isEmailSent">
                <input
                  :type="showPasswordConfirm ? 'text' : 'password'"
                  v-model="confirmPassword"
                  placeholder="Confirm Password"
                  :class="['input', { 'is-invalid': passwordError }]"
                  @input="passwordError = null"
                />
                <button
                  type="button"
                  class="eye-btn"
                  @click="showPasswordConfirm = !showPasswordConfirm"
                >
                  <i :class="showPasswordConfirm ? 'mdi mdi-eye-off' : 'mdi mdi-eye'"></i>
                </button>
              </div>

              <select v-if="!isEmailSent" v-model="groupToSend" class="select-component" required>
                <!-- optional “clear” choice -->
                <option value="" disabled hidden>Select Role</option>

                <!-- render your groups dynamically -->
                <option v-for="group in availableGroups" :key="group" :value="group">
                  {{ toFriendlyRoleName(group) }}
                </option>
              </select>
              <div v-if="isEmailSent" class="fineprint">
                We sent a verification link to
                <strong>{{ email }}</strong>
                . After verifying, return to this page. You can close this tab.
              </div>
            </div>

            <v-btn
              block
              :color="isEmailSent ? 'green' : '#3677D3'"
              class="font-weight-bold login-btn mt-4"
              type="submit"
              :disabled="loadingCouponId"
            >
              {{ isEmailSent ? 'Back to Company Users' : 'Create Account' }}
            </v-btn>
          </v-form>
        </v-card-text>
      </v-card>

      <!-- Snackbar for feedback messages -->
      <v-snackbar v-model="snackbar" color="info" :timeout="3000">
        {{ message || passwordError }}
        <template v-slot:action="{ attrs }">
          <v-btn color="white" text v-bind="attrs" @click="snackbar = false">Close</v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </div>
</template>

<style scoped>
.v-container {
  height: 100vh;
}

.password-wrapper {
  position: relative;
  width: 100%;
}

.password-wrapper .input {
  width: 100%;
  padding-right: 2.5em;
  /* room for the button */
}

.eye-btn {
  position: absolute;
  top: 50%;
  right: 0.8em;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  cursor: pointer;
}

.eye-btn .mdi {
  font-size: 1.2em;
}

/* Layout ----------------------------------------------------------------- */
.page-wrapper {
  min-height: 100vh;
  display: grid;
  place-items: center;
  background: #fbfcff;
  width: 100%;
  /* soft page tint */
}

.select-component {
  border: 1px solid rgb(189, 188, 188) !important;

  height: 42px;
  width: 100%;
  border-radius: 8px !important;
  padding-left: 15px;
  padding-right: 15px;
}

.select-component {
  /* remove any “appearance: none” you may have */
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;

  padding-right: 2.5em;
  /* tweak this to taste */
  /* modern browsers */
  background:
    /* your new arrow — you can use an SVG or a font-icon if you prefer */ url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="10" height="6"><path fill="%23666" d="M0 0l5 6 5-6"/></svg>')
    no-repeat right 1em center;
}

.login-btn {
  border-radius: 8px;
  box-shadow: none;
  font-weight: 400 !important;
  text-transform: capitalize;
  letter-spacing: 0.1px;
}

/* Layout ----------------------------------------------------------------- */
.page-wrapper {
  min-height: 100vh;
  display: grid;
  place-items: center;
  background: white;
  width: 100%;
  /* soft page tint */
}

.loader-wrapper {
  height: 50vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.auth-container {
  width: 100%;
  max-width: 490px;
  padding-inline: 16px !important;
  /* override Vuetify default */
  position: relative;
}

/* Brand ------------------------------------------------------------------ */
.brand {
  font-weight: 700;
  margin-bottom: 24px;
  font-smooth: 4px !important;
  color: #1c293a;
}

/* Card ------------------------------------------------------------------- */
.auth-card {
  box-shadow:
    0 2px 4px 0 rgba(16, 24, 40, 0.03),
    0 4px 8px 0 rgba(16, 24, 40, 0.04),
    0 12px 24px 0 rgba(16, 24, 40, 0.05),
    0 24px 48px 0 rgba(16, 24, 40, 0.06);
  border-radius: 12px;
  padding: 48px 40px;
}

/* Headings & copy -------------------------------------------------------- */
.welcome {
  font-size: clamp(25px, 5vw, 38px);
  font-weight: 600;
  line-height: 1.05;
  color: #1c2739;
}

.subtitle {
  font-size: 16px;
  color: #2b3033;
  line-height: 1.45;
}

.fineprint {
  font-size: 14px;
  color: #192637;
  text-align: center;
  margin-top: 5px;
}

.subquestion {
  font-size: 18px;
  font-weight: 450;
  text-align: center;
}

/* Links ------------------------------------------------------------------ */
.link {
  color: #0051ba;
  cursor: pointer;
  font-weight: 600;
  transition: color 0.15s;
}

.link:hover {
  color: #0066ff;
  text-decoration: underline;
}

/* Inputs & button tweaks ------------------------------------------------- */

.input {
  height: 42px;
  border-radius: 8px;
  padding: 0px 15px;
  border: 1px solid rgb(189, 188, 188);
}

.input::placeholder {
  color: #192637;
  font-weight: 400;
}

/* Separator -------------------------------------------------------------- */
.separator {
  display: flex;
  align-items: center;
  gap: 12px;
}

.separator::before,
.separator::after {
  content: '';
  flex: 1;
  height: 1.5px;
  background: #d3dcdb;
}

.separator span {
  font-size: 14px;
  color: #667085;
}

/* Company portal link button -------------------------------------------- */
.login-company {
  justify-content: center;
  font-weight: 500 !important;
  text-transform: none;
  letter-spacing: 0.3px;
}

.input-div {
  display: flex;
  flex-direction: column;
  gap: 15px;
}

input:-webkit-autofill,
textarea:-webkit-autofill,
select:-webkit-autofill {
  /* Background color */
  background-color: #fff !important;
  /* Text color */
  color: #000 !important;
  /* Remove the inner shadow that Chrome adds */
  -webkit-box-shadow: 0 0 0px 1000px #fff inset !important;
  /* You can also reset the transition delay to prevent flash */
  transition: background-color 5000s ease-in-out 0s !important;
}

/* If you use a different border color on focus, you may need to restyle it, too */
input:-webkit-autofill:focus,
textarea:-webkit-autofill:focus,
select:-webkit-autofill:focus {
  /* Example: keep your normal focus ring */
  box-shadow: 0 0 0 2px #636ae8;
}
</style>
